import Swal from "sweetalert2";
import { PASSENGERS_CSV_TEMPLATE_URL, PASSENGERS_CSV_GUIDE_URL } from "../core/constants/passengers";
import { buildImportPassengersMessage } from "../core/services/passenger.service";
const { isEmpty } = require("lodash");
const moment = require("moment");

/**
 * Atributos que no se permiten en un objeto de reserva para transferencias bancarias.
 */
const EXCLUDED_ATTRIBUTES = [
  "_id",
  "created_at",
  "updatedAt",
  "completed",
  "isProssesing",
  "deleted",
  "show",
  "remember",
  "invoice",
  "invoice_number",
  "booking_number",
  "old_status",
  "status_update",
  "discount",
  "send_payment_link",
  "payment_link",
  "payment",
  "booking_reference",
  "__v",
];

export const SHARE_LINK_HEADERS = [
  { key: "bookingReference", label: "Booking Reference" },
  { key: "transferType", label: "Transfer Type" },
  { key: "vehicleType", label: "Vehicle Type" },
  { key: "pickupDate", label: "Pickup Date" },
  { key: "pickupTime", label: "Pickup Time" },
  { key: "flightNumber", label: "Flight Number" },
  { key: "from", label: "From" },
  { key: "to", label: "To" },
  { key: "passengersCount", label: "Number of Passengers" },
  { key: "passengerName", label: "Passenger Name" },
  { key: "status", label: "Status" },
  { key: "driver", label: "Driver" },
  { key: "trackingLink", label: "Tracking Link" },
];

export const PAYMENT_METHODS = [
  { value: "ota", label: "OTA" },
  { value: "card", label: "Credit Card" },
  { value: "transfer", label: "Bank Transfer" },
  { value: "credit", label: "Credit" },
];

export const RESERVATION_OPTIONS = [
  { value: "arrival", label: "Airport (Arrival)" },
  { value: "departure", label: "Airport (Departure)" },
  { value: "station", label: "Train" },
  { value: "port", label: "Port" },
  { value: "hourly", label: "Hourly" },
  { value: "journey", label: "Journey" },
];

export const RESERVATION_TYPES = [
  "arrival",
  "departure",
  "station",
  "port",
  "hourly",
  "journey",
];

/** Calculate the difference in hours between the pickup date and the current time in the specified time zone.
 * @param {Date} pickupDate - The pickup date of the reservation.
 * @param {string} timeZone - The time zone to consider for the calculation.
 * @returns {number} - The difference in hours.
 */
export function calculateHoursDifference(pickupDate, timeZone) {
  const bookingDate = moment.parseZone(pickupDate);
  const now = moment.tz(timeZone);
  return now.diff(bookingDate, "hours", true);
}

/** Determine if a booking can be confirmed based on its status, status update, and owner role.
 * @param {string} bookingStatus - The current status of the booking.
 * @param {boolean} statusUpdate - Whether a status update is requested.
 * @param {string} owner - The role of the user (e.g., "admin").
 * @returns {boolean} - True if the booking can be confirmed, false otherwise.
 */
export function canConfirmBooking(bookingStatus, statusUpdate, owner) {
  let values = [
    "SC",
    "SBC",
    "SCB",
    "NS",
    "DA",
    "JF",
    "POB",
    "DSR",
    "DAP",
    "DRR",
  ];
  if (owner === "admin") {
    return statusUpdate && bookingStatus === "SC"
      ? true
      : !values.includes(bookingStatus);
  } else {
    return false;
  }
}

/** Determine if a booking can be canceled based on its status.
 * @param {string} bookingStatus - The current status of the booking.
 * @returns {boolean} - True if the booking can be canceled, false otherwise.
 */
export function canCancelBooking(bookingStatus) {
  if (!["SBC", "SCB", "NS", "JF"].includes(bookingStatus)) {
    return true;
  } else {
    return false;
  }
}

/** Determine if a booking can be completed based on its status.
 * @param {string} bookingStatus - The current status of the booking.
 * @returns {boolean} - True if the booking can be completed, false otherwise.
 */
export function canCompleteBooking(bookingStatus) {
  if (!["SBC", "SCB", "JF", "NS", "PNDG"].includes(bookingStatus)) {
    return true;
  } else {
    return false;
  }
}

/** Determine if a booking can be marked as no-show based on its status.
 * @param {string} bookingStatus - The current status of the booking.
 * @returns {boolean} - True if the booking can be marked as no-show, false otherwise.
 */
export function canMarkBookingAsNoShow(bookingStatus) {
  if (
    ![
      "SBC",
      "SCB",
      "JF",
      "NS",
      "SRB",
      "POB",
      "DRR",
      "NW",
      "DA",
      "PNDG",
    ].includes(bookingStatus)
  ) {
    return true;
  } else {
    return false;
  }
}

/** Determine if a booking can be marked as paid based on its status, payment, and rate status.
 * @param {string} bookingStatus - The current status of the booking.
 * @param {Object|undefined} payment - The payment object (undefined if no payment).
 * @param {boolean} rateStatus - The status of the rate (true if valid).
 * @returns {boolean} - True if the booking can be marked as paid, false otherwise.
 */
export function canMarkBookingAsPaid(bookingStatus, payment, rateStatus) {
  if (!["NS"].includes(bookingStatus) && !payment) {
    if (bookingStatus === "PNDG" && rateStatus) {
      return false;
    } else {
      return true;
    }
  } else {
    return false;
  }
}

/**
 * Determine if a booking email can be resent based on its status.
 * @param {*} bookingStatus - The current status of the booking.
 * @returns {boolean} - True if the email can be resent, false otherwise.
 */
export function canResendBookingEmail(bookingStatus) {
  return ["SRB", "POB", "DRR", "NW", "DA", "SC"].includes(bookingStatus);
}

/** Determine if a voucher can be downloaded based on the booking status.
 * @param {string} bookingStatus - The current status of the booking.
 * @returns {boolean} - True if the voucher can be downloaded, false otherwise.
 */

export function canDownloadVoucher(bookingStatus) {
  return !["PNDG", "BI"].includes(bookingStatus);
}

/** Determine if a quote can be downloaded based on the booking status.
 * @param {string} bookingStatus - The current status of the booking.
 * @returns {boolean} - True if the quote can be downloaded, false otherwise.
 */
export function canDownloadQuote(bookingStatus) {
  return ["BI"].includes(bookingStatus);
}

/**
 * Check if there are any completed bookings.
 * @param {Array} bookings - The list of bookings to check.
 * @returns {boolean} - True if there are completed bookings, false otherwise.
 */
export function hasCompletedBookings(bookings = []) {
  return bookings.some((booking) => booking.status === "JF");
}

/** Check if there are any bookings with payments.
 * @param {Array} bookings - The list of bookings to check.
 * @returns {boolean} - True if there are bookings with payments, false otherwise.
 */
export function hasPaidBookings(bookings = []) {
  return bookings.some((booking) => booking.payment !== null);
}

/** Check if there are any draft bookings.
 * @param {Array} bookings - The list of bookings to check.
 * @returns {boolean} - True if there are draft bookings, false otherwise.
 */
export function hasDraftBookings(bookings = []) {
  return bookings.some((booking) => booking.status === "BI");
}

/** Check if there are any canceled bookings.
 * @param {Array} bookings - The list of bookings to check.
 * @returns {boolean} - True if there are canceled bookings, false otherwise.
 */
export function hasCanceledBookings(bookings = []) {
  return bookings.some((booking) =>
    ["SCB", "SBC", "CANCELLED"].includes(booking.status)
  );
}

/** Check if there are any bookings with payment links sent or open.
 * @param {Array} bookings - The list of bookings to check.
 * @returns {boolean} - True if there are bookings with payment links, false otherwise.
 */
export function hasBookingsWithPaymentLink(bookings = []) {
  return bookings.some((booking) =>
    ["sent", "open"].includes(booking.send_payment_link)
  );
}

/** Check if there are any expired bookings.
 * @param {Array} bookings - The list of bookings to check.
 * @returns {boolean} - True if there are expired bookings, false otherwise.
 */
export function hasExpiredBookings(bookings = []) {
  return bookings.some((booking) => {
    if (booking.class === "Experiences") {
      return ["EXPIRED"].includes(booking.status);
    } else {
      return ["PNDG"].includes(booking.status) && booking.rate.status;
    }
  });
}

/** Check if there are any bookings related to experiences.
 * @param {Array} bookings - The list of bookings to check.
 * @returns {boolean} - True if there are bookings related to experiences, false otherwise.
 */
export function hasExperiencesBookings(bookings = []) {
  return bookings.some(
    (booking) => booking.class === "Experiences" || booking.product
  );
}

/** Get the user ID from a user object or string.
 * @param {Object|string|null} user - The user object or string.
 * @returns {string|null} - The user ID or null if not available.
 */
export function getUserId(user) {
  if (user) {
    return typeof user === "string" ? user : user._id;
  }
  return null;
}

/** Check if all bookings have the same user for a specified key.
 * @param {Array} bookings - The list of bookings to check.
 * @param {string} key - The key to check (e.g., 'customer', 'agent').
 * @returns {boolean} - True if all bookings have the same user for the specified key, false otherwise.
 */
export function allBookingsHaveSame(bookings = [], key) {
  if (bookings.length <= 1) {
    return true;
  }
  const userIds = new Set(bookings.map((booking) => getUserId(booking[key])));
  return userIds.size === 1;
}

/** Check if all bookings have the same user for both customer and agent.
 * @param {Array} bookings - The list of bookings to check.
 * @returns {boolean} - True if all bookings have the same user for both customer and agent, false otherwise.
 */
export function isSameUserForAllBookings(bookings = []) {
  return (
    !allBookingsHaveSame(bookings, "customer") &&
    !allBookingsHaveSame(bookings, "agent")
  );
}

/** Remove excluded attributes from a booking object for bank transfer purposes.
 * @param {Object} booking - The booking object.
 * @returns {Object} - The new booking object without the excluded attributes.
 */
export function removeTransferExcludedAttributes(booking) {
  const newBooking = { ...booking };
  EXCLUDED_ATTRIBUTES.forEach((attribute) => {
    delete newBooking[attribute];
  });
  return newBooking;
}

/** Convert time units to hours.
 * @param {string} unit - The time unit ("MINUTES", "HOURS", "DAYS").
 * @param {number} value - The value to convert.
 * @returns {number} - The converted value in hours.
 */
export function convertTimeUnits(unit, value) {
  if (unit === "MINUTES") {
    return value / 60;
  } else if (unit === "DAYS") {
    return value * 24;
  } else {
    return value;
  }
}

/** Get the display format for a time unit.
 * @param {string} unit - The time unit ("MINUTES", "HOURS", "DAYS").
 * @returns {string} - The display format for the unit.
 */
export function getUnitFormat(unit) {
  if (unit === "HOURS") {
    return "hours";
  } else if (unit === "DAYS") {
    return "days";
  } else {
    return "months";
  }
}

/**
 * Resolves the applicable refund type and percentage from a cancellation policy.
 * Evaluates FULL_REFUND, tiered PARTIAL_REFUND ranges, and NO_REFUND in priority order.
 * @param {Date} utcNow - The current UTC date and time.
 * @param {Date} pickupDate - The pickup date of the reservation.
 * @param {Object} cancellationPolicy - OCTO cancellation policy with `refundEligibility` array and `nonRefundable` flag.
 * @returns {Object} - `{ refundable: boolean, percent: number, type: string }`.
 */
export function resolveRefundEligibility(utcNow, pickupDate, cancellationPolicy) {
  const refundEligibility = cancellationPolicy.refundEligibility || [];
  const nonRefundable = cancellationPolicy.nonRefundable || false;

  if (nonRefundable) {
    return { refundable: false, percent: 0, type: "NON_REFUNDABLE" };
  }

  const diffTime = moment(pickupDate).diff(utcNow, "hours", true);

  const fullRefund = refundEligibility.find(
    e => e.type === "FULL_REFUND" && diffTime > convertTimeUnits(e.unit, e.cutoff)
  );
  if (fullRefund) {
    return { refundable: true, percent: fullRefund.percent, type: fullRefund.type };
  }

  const partials = refundEligibility
    .filter(e => e.type === "PARTIAL_REFUND")
    .sort((a, b) => convertTimeUnits(b.unit, b.cutoff) - convertTimeUnits(a.unit, a.cutoff));

  for (let i = 0; i < partials.length; i++) {
    const current = convertTimeUnits(partials[i].unit, partials[i].cutoff);
    const upper = i === 0 ? Infinity : convertTimeUnits(partials[i - 1].unit, partials[i - 1].cutoff);
    if (diffTime <= upper && diffTime >= current) {
      return { refundable: true, percent: partials[i].percent, type: partials[i].type };
    }
  }

  const noRefund = refundEligibility.find(
    e => e.type === "NO_REFUND" && diffTime < convertTimeUnits(e.unit, e.cutoff)
  );
  if (noRefund) {
    return { refundable: false, percent: noRefund.percent, type: noRefund.type };
  }

  return { refundable: false, percent: 0, type: "NO_REFUND" };
}

/**
 * Confirm a booking action with a SweetAlert2 dialog.
 * @param {*} bookingId - The ID of the booking to confirm.
 * @param {*} callback - The callback function to execute after confirmation.
 */
export async function confirmBooking(bookingId, callback) {
  let { value } = await Swal.fire({
    html: "Are you sure you want to <strong>Confirm</strong> this reservation?",
    title: "Confirm Reservation",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    showLoaderOnConfirm: true,
    timerProgressBar: true,
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookingId);
  }
}

/**
 * Confirm a booking action with a SweetAlert2 dialog.
 * @param {*} bookingId - The ID of the booking to confirm.
 * @param {*} callback - The callback function to execute after confirmation.
 */
export async function completeBooking(bookingId, callback) {
  let { value } = await Swal.fire({
    title: "Complete Reservation",
    html: "Are you sure you want to mark this reservation as <br><strong>Completed</strong>?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookingId, "JF", "");
  }
}

/** Mark a booking as no-show with a SweetAlert2 dialog.
 * @param {*} bookingId - The ID of the booking to mark as no-show.
 * @param {*} callback - The callback function to execute after marking as no-show.
 */
export async function noShowBooking(bookingId, callback) {
  const { value } = await Swal.fire({
    title: "No Show Reservation",
    html: "Are you sure you want to mark this reservation as <br><strong>No Show</strong>?, <br><br> To continue select a Reason:",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    input: "radio",
    inputOptions: {
      "Passenger didn't show": "Passenger didn't show",
      "Driver didn't show": "Driver didn't show",
    },
    inputPlaceholder: "Select a reason",
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    inputClass: "",
    inputValidator: (value) => {
      return new Promise((resolve) => {
        if (!value) {
          resolve("You need to select a reason");
        } else {
          resolve();
        }
      });
    },
  });
  if (value) {
    await callback(bookingId, "NS", value);
  }
}

/** Duplicate a booking with a SweetAlert2 dialog.
 * @param {*} bookingId - The ID of the booking to duplicate.
 * @param {*} callback - The callback function to execute after duplication.
 */
export async function duplicateBooking(bookingId, callback) {
  let { value } = await Swal.fire({
    title: "Duplicate Reservation",
    html: "Are you sure you want to <strong>duplicate</strong> this Reservation?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookingId, true);
  }
}

/** Mark multiple reservations as paid with a SweetAlert2 dialog.
 * @param {Array} bookings - The list of bookings to mark as paid.
 * @param {function} callback - The callback function to execute after marking as paid.
 */
export async function markReservationsAsPaid(bookings = [], callback) {
  let { value } = await Swal.fire({
    title: "Pay Reservation",
    html: "Are you sure you want to <strong>Pay</strong> this reservation?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    allowOutsideClick: false,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookings.map((booking) => booking._id));
  }
}

/** Logic for resend notifications */

// Define constants for notification recipients and reservation classes
const NOTIFICATION_RECIPIENTS = {
  CUSTOMER: "customer-user",
  PASSENGER: "passenger-user",
  SUPPLIER: "supplier-user",
  PARTNER: "partner-user",
};

// Define reservation classes
const RESERVATION_CLASSES = {
  TRANSFERS: "Transfers",
  OTHER: "Experiences",
};
// Define statuses for transfers
const TRANSFERS_STATUS = ["NW", "SRB"];

// Function to generate checkbox HTML
const getCheckboxHTML = (id, label) => `
    <div class="custom-control custom-checkbox">
        <input class="custom-control-input" type="checkbox" id="${id}" name="options">
        <label for="${id}" class="custom-control-label">${label}</label>
    </div>
`;

// Map recipients to their corresponding checkbox HTML
const RECIPIENT_HTML = {
  [NOTIFICATION_RECIPIENTS.CUSTOMER]: getCheckboxHTML(
    NOTIFICATION_RECIPIENTS.CUSTOMER,
    "Customer"
  ),
  [NOTIFICATION_RECIPIENTS.PASSENGER]: getCheckboxHTML(
    NOTIFICATION_RECIPIENTS.PASSENGER,
    "Passenger"
  ),
  [NOTIFICATION_RECIPIENTS.SUPPLIER]: getCheckboxHTML(
    NOTIFICATION_RECIPIENTS.SUPPLIER,
    "Supplier"
  ),
  [NOTIFICATION_RECIPIENTS.PARTNER]: getCheckboxHTML(
    NOTIFICATION_RECIPIENTS.PARTNER,
    "Partner"
  ),
};

// Logic to determine recipients based on reservation class and status
const RECIPIENT_LOGIC = {
  [RESERVATION_CLASSES.TRANSFERS]: (reservationStatus, isProssesing) =>
    TRANSFERS_STATUS.includes(reservationStatus) && isProssesing
      ? [NOTIFICATION_RECIPIENTS.SUPPLIER]
      : [
        NOTIFICATION_RECIPIENTS.CUSTOMER,
        NOTIFICATION_RECIPIENTS.PASSENGER,
        NOTIFICATION_RECIPIENTS.SUPPLIER,
        NOTIFICATION_RECIPIENTS.PARTNER,
      ],
  [RESERVATION_CLASSES.OTHER]: () => [
    NOTIFICATION_RECIPIENTS.CUSTOMER,
    NOTIFICATION_RECIPIENTS.PASSENGER,
    NOTIFICATION_RECIPIENTS.PARTNER,
  ],
};

// Function to generate HTML for the resend notifications dialog
function getHTMLToResendNotifications(
  bookingStatus,
  bookingType,
  isProcessing
) {
  const recipients = RECIPIENT_LOGIC[bookingType]
    ? RECIPIENT_LOGIC[bookingType](bookingStatus, isProcessing)
    : RECIPIENT_LOGIC[RESERVATION_CLASSES.OTHER]();

  const recipientCheckboxesHTML = recipients
    .map((recipient) => RECIPIENT_HTML[recipient])
    .join("");

  return `
    Are you sure you want to <strong>resend a notification?</strong>
    <div class='space'></div>
    To continue select who you want to send the email to:
    <div class="text-left pl-2 pt-2">
        ${recipientCheckboxesHTML}
    </div>`;
}

// Function to get the final list of recipients based on selected checkboxes
function getRecipients(customer, partner, passenger, supplier) {
  let recipients = [];
  if (customer) {
    recipients.push("CUSTOMER");
  }
  if (passenger) {
    recipients.push("PASSENGER");
  }
  if (partner) {
    recipients.push("AGENT");
  }
  if (supplier) {
    recipients.push("SUPPLIER");
  }
  return recipients;
}

/** Resend notifications for a booking.
 * @param {string} bookingId - The ID of the booking.
 * @param {string} bookingStatus - The current status of the booking.
 * @param {string} bookingType - The type of the booking (e.g., "Transfers", "Experiences").
 * @param {boolean} isProcessing - Whether the booking is in processing state.
 * @param {function} callback - The callback function to execute after confirmation.
 */
export async function resendNotifications(
  bookingId,
  bookingStatus,
  bookingType,
  isProcessing,
  callback
) {
  const { value } = await Swal.fire({
    title: "Resend Notification",
    html: getHTMLToResendNotifications(
      bookingStatus,
      bookingType,
      isProcessing
    ),
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    preConfirm: () => {
      // Determine recipients based on selected checkboxes
      const recipients = RECIPIENT_LOGIC[bookingType]
        ? RECIPIENT_LOGIC[bookingType](bookingStatus, isProcessing)
        : RECIPIENT_LOGIC[RESERVATION_CLASSES.OTHER]();

      // Map checked checkboxes to their corresponding recipients
      const checkedRecipients = recipients.filter(
        (recipient) => Swal.getPopup().querySelector(`#${recipient}`).checked
      );
      // Function to get the final list of recipients
      return getRecipients(
        checkedRecipients.includes(NOTIFICATION_RECIPIENTS.CUSTOMER),
        checkedRecipients.includes(NOTIFICATION_RECIPIENTS.PARTNER),
        checkedRecipients.includes(NOTIFICATION_RECIPIENTS.PASSENGER),
        checkedRecipients.includes(NOTIFICATION_RECIPIENTS.SUPPLIER)
      );
    },
  });
  if (value) {
    await callback(bookingId, value);
  }
}

/**
 * Get the cancellation policy text based on refund eligibility.
 * @param {Object} refundEligibility - The refund eligibility object.
 * @return {string} - The cancellation policy text.
 */

export function getPolicyCancellationText(refundEligibility) {
  if (refundEligibility.type === "FULL_REFUND") {
    return "with Full Refund";
  } else if (refundEligibility.type === "PARTIAL_REFUND") {
    return `with Partial Refund of ${refundEligibility.percent}%`;
  } else if (refundEligibility.type === "NO_REFUND") {
    return "No-Refund";
  } else {
    return "Non-Refundable";
  }
}

/**
 * Generate the HTML content for the SweetAlert confirmation dialog.
 * @param {Object} refundEligibility - The refund eligibility object.
 * @return {string} - The generated HTML content.
 */
function generateCancelBookingHtml(refundEligibility) {
  return `
    <span class='span-note f14 d-block'>Cancellation <strong class="danger">${getPolicyCancellationText(
    refundEligibility
  )}</strong></span>
    <div class='space'></div>
    Are you sure you want to <strong>Cancel</strong> this reservation?
    <div class='space'></div> To continue select a <strong>Reason:</strong>
    <div class='space'></div>
    <div class="col-12 mt-2 mb-3">
      <label id="label-reason" class="title-label" for="select-reason">Reason</label>
      <select id="select-reason" class="input-custom">
        <option value="" disabled selected></option>
        <option value="Cancelled Flight">Cancelled Flight</option>
        <option value="Travel Plans Changed">Travel Plans Changed</option>
        <option value="Flight Delayed">Flight Delayed</option>
        <option value="Schedule Change">Schedule Change</option>
        <option value="Duplicate Booking">Duplicate Booking</option>
        <option value="Change of Plans">Change of Plans</option>
        <option value="Incorrect Info">Incorrect Info</option>
        <option value="Price Issue">Price Issue</option>
        <option value="Reseller Cancellation">Reseller Cancellation</option>
        <option value="Weather Issues">Weather Issues</option>
        <option value="Personal Reasons">Personal Reasons</option>
        <option value="Other">Other</option>
      </select>
    </div>
    <div class="col-12 mb-3" id="box-other" style="display: none;">
      <label id="label-other" class="title-label" for="input-other">Other Reason</label>
      <textarea id="input-other" maxlength="200" rows="1" class="input-custom" placeholder="Enter your reason"></textarea>
    </div>
  `;
}

/** Cancel a booking with a SweetAlert2 dialog.
 * @param {string} bookingId - The ID of the booking to cancel.
 * @param {Object} refundEligibility - The refund eligibility object.
 * @param {function} callback - The callback function to execute after cancellation.
 */
export async function cancelBooking(bookingId, refundEligibility, callback) {
  const { value } = await Swal.fire({
    title: "Cancel Reservation",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    html: generateCancelBookingHtml(refundEligibility),
    focusConfirm: false,
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    reverseButtons: true,
    allowOutsideClick: false,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    didOpen: () => {
      const selectElement = document.getElementById("select-reason");
      const otherInputElement = document.getElementById("box-other");
      selectElement.addEventListener("change", () => {
        otherInputElement.style.display =
          selectElement.value === "Other" ? "block" : "none";
      });
    },
    preConfirm: () => {
      try {
        const selectReason = document.getElementById("select-reason").value;
        const otherReason = Swal.getPopup().querySelector("#input-other").value;

        if (!selectReason) {
          document.getElementById("label-reason").style.color = "red";
          document.getElementById("select-reason").style.border =
            "1px solid red";
          Swal.showValidationMessage("Select a reason");
          return false;
        }
        if (selectReason === "Other" && !otherReason) {
          document.getElementById("label-other").style.color = "red";
          document.getElementById("input-other").style.border = "1px solid red";
          Swal.showValidationMessage("Enter a reason");
          return false;
        }
        return selectReason === "Other" ? otherReason : selectReason;
      } catch (error) {
        return "";
      }
    },
  });
  if (value) {
    await callback(bookingId, value, refundEligibility);
  }
}

/** Cancel an OTA booking with a SweetAlert2 dialog.
 * @param {string} bookingId - The ID of the OTA booking to cancel.
 * @param {function} callback - The callback function to execute after cancellation.
 */
export async function otaCancelBooking(bookingId, callback) {
  let { value } = await Swal.fire({
    html: "Are you sure you want to <strong>Cancel</strong> this reservation?",
    title: "Cancel Reservation",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    showLoaderOnConfirm: true,
    timerProgressBar: true,
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookingId, "reason");
  }
}

/** Send a payment link with a SweetAlert2 dialog.
 * @param {Object} payload - The payload for sending the payment link.
 * @param {function} callback - The callback function to execute after sending the payment link.
 */
export async function sendPaymentLink(payload, callback) {
  let { value } = await Swal.fire({
    title: "Send a payment link",
    html: "Are you sure you want to <strong>send a payment link</strong> email for the selected reservations?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    allowOutsideClick: false,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => setTimeout(() => { }, 1500),
  });
  if (value) {
    await callback(payload);
  }
}
/** Delete a booking with a SweetAlert2 dialog.
 * @param {string} bookingId - The ID of the booking to delete.
 * @param {function} callback - The callback function to execute after deletion.
 */
export async function deleteBooking(bookingId, callback) {
  let { value } = await Swal.fire({
    title: "Delete Reservation",
    html: "Are you sure you want to <strong>Delete</strong> this Reservation?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/warning.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookingId);
  }
}

/** Bulk delete bookings with a SweetAlert2 dialog.
 * @param {Array} bookings - The list of bookings to delete.
 * @param {function} callback - The callback function to execute after deletion.
 */
export async function bulkDeleteBookings(bookings, callback) {
  let { value } = await Swal.fire({
    title: "Delete Reservations",
    html: "Are you sure you want to <strong>Delete</strong> the selected Reservations?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/warning.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookings);
  }
}

/** Escape a value for CSV format.
 * @param {string|number|null} value - The value to escape.
 * @returns {string} - The escaped value.
 */
export function escapeCSV(value) {
  if (value == null) return "";
  let str = String(value);
  if (str.includes('"')) {
    str = str.replace(/"/g, '""');
  }
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    str = `"${str}"`;
  }
  return str;
}

/** Convert an array of objects to CSV format.
 * @param {Array} content - The array of objects to convert.
 * @param {Array} header - The header configuration with key and label.
 * @returns {string} - The CSV formatted string.
 * @throws {Error} - If content or header is not a non-empty array.
 */
export function convertToCSV(content, header) {
  if (!Array.isArray(content) || !content.length) {
    throw new Error('convertToCSV: "content" must be a non-empty array.');
  }
  if (!Array.isArray(header) || !header.length) {
    throw new Error(
      'convertToCSV: "header" must be a non-empty array of {key,label}.'
    );
  }

  const titles = header.map((h) => escapeCSV(h.label));
  const keys = header.map((h) => h.key);

  const rows = content.map((item) =>
    keys.map((k) => escapeCSV(item[k])).join(",")
  );

  return [titles.join(","), ...rows].join("\n");
}

/**
 * Download a CSV file with the given text and filename.
 * @param {string} csvText - The CSV content as a string.
 * @param {string} [filename='export.csv'] - The name of the file to be downloaded.
 * @returns {string|null} - The URL of the created object or null if an error occurred.
 */
export function downloadCSV(csvText, filename = "export.csv") {
  try {
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return url;
  } catch (error) {
    console.error("Error downloading CSV:", error);
    return null;
  }
}

/** Get airport details based on booking type and options.
 * @param {string} bookingType - The type of booking ("arrival" or "departure").
 * @param {Array} optionsFrom - The array of options for the "from" location.
 * @param {Array} optionsTo - The array of options for the "to" location.
 * @returns {Object|null} - The metadata of the relevant option or null if not found.
 */
export function getAirportDetails(bookingType, optionsFrom, optionsTo) {
  if (bookingType === "arrival") {
    let [optionFrom] = optionsFrom;
    if (optionFrom) {
      return !isEmpty(optionFrom.metadata) ? optionFrom.metadata : null;
    }
    return null;
  } else if (bookingType === "departure") {
    let [optionTo] = optionsTo;
    if (optionTo) {
      return !isEmpty(optionTo.metadata) ? optionTo.metadata : null;
    }
    return null;
  } else {
    return null;
  }
}

/**
 * Determine flight type based on booking type
 * @param {String} bookingType - The type of booking (arrival/departure)
 * @returns {String} - The flight type (ARRIVAL/DEPARTURE/NONE)
 */
export function getFlightType(bookingType) {
  if (bookingType === "arrival") {
    return "ARRIVAL";
  } else if (bookingType === "departure") {
    return "DEPARTURE";
  }
  return "NONE";
}

/** Populate "from" options based on booking type and location data.
 * @param {string} from - The "from" location.
 * @param {string} type - The booking type ("arrival" or "departure").
 * @param {Object} location - The location data object.
 * @returns {Array} - An array of populated "from" options.
 */
export function populateFromOptions(from, type, location) {
  if (from) {
    let isArrival = type === "arrival" && location;
    if (isArrival) {
      return [
        {
          select: from,
          type: location.type,
          country_code: location.country_code,
          location: location.location,
          place_id: location.place_id,
          owner: location.owner,
          metadata: location.metadata ? location.metadata : {},
        },
      ];
    }
    return [
      {
        select: from,
        type: ["airport", "arrival"].includes(type) ? "airport" : "",
        country_code: "",
        metadata: {},
      },
    ];
  }
  return [];
}

/** Populate "to" options based on booking type and location data.
 * @param {string} to - The "to" location.
 * @param {string} type - The booking type ("arrival" or "departure").
 * @param {Object} location - The location data object.
 * @returns {Array} - An array of populated "to" options.`
 */
export function populateToOptions(to, type, location) {
  if (to) {
    let isDeparture = type === "departure" && location;
    if (isDeparture) {
      return [
        {
          select: to,
          type: location.type,
          country_code: location.country_code,
          location: location.location,
          place_id: location.place_id,
          owner: location.owner,
          metadata: location.metadata ? location.metadata : {},
        },
      ];
    }
    return [
      {
        select: to,
        type: ["airport", "departure"].includes(type) ? "airport" : "",
        country_code: "",
        metadata: {},
      },
    ];
  }
  return [];
}
/** Get airport name based on booking type.
 * @param {string} bookingType - The type of booking ("arrival" or "departure").
 * @param {string} from - The "from" location.
 * @param {string} to - The "to" location.
 * @returns {string} - The airport name.
 */
export function getAirportName(bookingType, from, to) {
  if (bookingType === "arrival") {
    return from ? from : "";
  } else if (bookingType === "departure") {
    return to ? to : "";
  }
  return "";
}

/** Format a date to "DD-MMM-YYYY" format in UTC.
 * @param {Date|string} date - The date to format.
 * @returns {string} - The formatted date string.
 */
export function getFormatDate(date) {
  return moment(date).utc(true).format("DD-MMM-YYYY");
}

/** Format a time to "hh:mm A" format.
 * @param {string} time - The time to format.
 * @returns {string} - The formatted time string.
 */
export function getFormatTime(time) {
  return moment(time, ["hh:mm A"]).format("hh:mm A");
}

/** Get label of the transport reference according the booking type.
 * @param {string} bookingType - The type of booking ("arrival", "departure","station", "port").
 * @returns {string} - The transport reference label.
 */
export function getLabelTransport(bookingType) {
  if (["airport", "arrival", "departure"].indexOf(bookingType) > -1) {
    return "Flight N°";
  } else if (bookingType === "station") {
    return "Rail Line";
  } else if (bookingType === "port") {
    return "Cruise Ship";
  } else if (bookingType === "hourly") {
    return "Hours";
  }
  return "";
}

/** Get transport reference according the booking type.
 * @param {string} booking - The  booking all data
 * @returns {string} - The transport reference number according to booking type ("arrival", "departure","station", "port").
 */
export function getTransportReference(booking) {
  if (["airport", "arrival", "departure"].includes(booking.type)) {
    return booking.flight_number;
  } else if (booking.type === "station") {
    return booking.number_stations;
  } else if (booking.type === "port") {
    return booking.cruise_line;
  } else if (booking.type === "hourly") {
    return `${booking.number_hourly} hours`;
  }
  return "";
}

/**
  * Bulk update reservations with a SweetAlert2 dialog.
 * @param {*} callback - The callback function to execute after confirmation.
 */
export async function importReservationsFromCSV(callback = () => { }) {
  let { value } = await Swal.fire({
    title: "Import Reservations from CSV",
    html: "This will upload and process the CSV file to create reservations. Do you want to continue?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    }
  });
  if (value) {
    await callback();
  }
}

/** * Export events template with a SweetAlert2 dialog.
 * @param {Array} ids - The list of event IDs to include in the template.
 * @param {function} callback - The callback function to execute after confirmation.
 */
export async function exportEventsTemplate(ids = [], callback = () => { }) {
  let { value } = await Swal.fire({
    title: "Export Events Template",
    html: "This will download a CSV template for events. Do you want to continue?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    }
  });
  if (value) {
    await callback(ids);
  }
}

/** * Confirm the passengers import with a SweetAlert2 dialog that links to the CSV template.
 * @param {function} callback - The callback function to execute after confirmation.
 */
export async function confirmImportPassengers(callback = () => { }) {
  let { value } = await Swal.fire({
    title: "Import Passengers",
    html: buildImportPassengersMessage(PASSENGERS_CSV_TEMPLATE_URL, PASSENGERS_CSV_GUIDE_URL),
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/info.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "Close",
    confirmButtonText: "Import",
    onClose: () => {
      setTimeout(() => { }, 1500);
    }
  });
  if (value) {
    callback();
  }
}
